import { Link } from 'react-router-dom';
import type { Project } from '@shared/api-client';
import { Check, Circle, RefreshCw } from 'lucide-react';
import { useSetupSignals } from '@/hooks/use-setup-signals';

export const SetupSignalStatus = ({
  project,
  compact = false,
  status: suppliedStatus,
}: {
  project: Project;
  compact?: boolean;
  status?: ReturnType<typeof useSetupSignals>;
}) => {
  const observedStatus = useSetupSignals(project, !suppliedStatus);
  const status = suppliedStatus ?? observedStatus;
  if (!project.profile?.trackingPlan) {
    return null;
  }
  return (
    <section
      aria-label="Tracking status"
      className="space-y-4 border-y border-border py-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">
          {status.signalsReceived
            ? 'Tracking is receiving data'
            : 'Tracking status'}
        </h2>
        {compact && (
          <Link
            to={`/projects/${project.id}/setup`}
            className="text-sm font-medium text-accent underline underline-offset-4"
          >
            Review setup
          </Link>
        )}
      </div>
      <p className="text-sm leading-relaxed text-ink-muted">
        {status.signalsReceived
          ? 'Your selected activity is reaching GainSignal.'
          : 'We’ll update this automatically as events arrive.'}
      </p>
      {!status.mobile && (
        <SignalRow
          done={status.websiteReceived}
          label={
            status.websiteReceived
              ? 'Website activity received'
              : 'Waiting for website activity'
          }
          detail={
            status.realtime.isError
              ? 'Couldn’t check recent activity.'
              : 'Pageviews received in the last 30 minutes.'
          }
          retry={
            status.realtime.isError
              ? () => void status.realtime.refetch()
              : undefined
          }
        />
      )}
      {status.goals.map((goal) => (
        <SignalRow
          key={goal.eventName}
          done={!goal.error && goal.observation === 'received'}
          label={goal.name}
          detail={
            goal.error
              ? 'Couldn’t check this goal.'
              : goal.loading
                ? 'Checking received events…'
                : !goal.prepared
                  ? 'The matching goal is missing. Prepare your setup again.'
                  : goal.observation === 'received'
                    ? `Received${project.profile?.setupPreparedAt ? ' since setup' : ' in the last 7 days'}.`
                    : goal.observation === 'historical'
                      ? 'Earlier data exists · waiting for a new event.'
                      : 'Waiting for the first event.'
          }
          retry={goal.error ? () => void goal.retry() : undefined}
        />
      ))}
      {status.providers.length > 0 && (
        <SignalRow
          done={status.revenueConnected}
          label={
            status.revenueConnected
              ? 'Revenue provider connected'
              : 'Revenue connection needed'
          }
          detail={
            status.integrations.isError
              ? 'Couldn’t check the revenue connection.'
              : status.revenueConnected
                ? 'Confirm payment delivery in your revenue reports.'
                : 'Continue to integrations to connect your billing provider.'
          }
          retry={
            status.integrations.isError
              ? () => void status.integrations.refetch()
              : undefined
          }
        />
      )}
      {!status.signalsReceived && !compact && (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-ink">
            Help with missing signals
          </summary>
          <div className="mt-3 space-y-3 leading-relaxed text-ink-muted">
            <p>
              Open the deployed product with analytics consent enabled. Ask your
              agent to check script loading, Content Security Policy, and the
              exact hostname. Queued requests are not proof that events reached
              analytics.
            </p>
            {status.goals
              .filter((goal) => goal.observation !== 'received')
              .map((goal) => (
                <p key={goal.eventName}>
                  <span className="font-medium text-ink">{goal.name}:</span>{' '}
                  {goal.trigger} The matching event is{' '}
                  <code className="break-all">{goal.eventName}</code>. Use an
                  authorized test workflow and check retries or duplicate
                  callbacks.
                </p>
              ))}
            {status.mobile && (
              <p>
                Native delivery requires a compatible event transport. Your
                agent should identify the existing transport or report the
                concrete missing integration; browser activity does not prove
                native events work.
              </p>
            )}
            <p>
              Do not create real purchases or artificial production conversions
              to complete this checklist.
            </p>
          </div>
        </details>
      )}
    </section>
  );
};

const SignalRow = ({
  done,
  label,
  detail,
  retry,
}: {
  done: boolean;
  label: string;
  detail: string;
  retry?: () => void;
}) => (
  <div className="flex items-start gap-3" role="status">
    {done ? (
      <Check className="mt-0.5 size-4 shrink-0 text-positive" />
    ) : (
      <Circle className="mt-0.5 size-4 shrink-0 text-ink-muted" />
    )}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-ink">{label}</p>
      <p className="mt-1 text-sm leading-relaxed text-ink-muted">{detail}</p>
    </div>
    {retry && (
      <button
        type="button"
        onClick={retry}
        className="flex items-center gap-1 py-1 text-sm text-accent"
      >
        <RefreshCw className="size-3" /> Retry
      </button>
    )}
  </div>
);
